import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CloudOff, RefreshCw } from 'lucide-react'

import { useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, PosButton } from '@/components/ui'
import { useSyncState } from '@/contexts/SessionContext'
import { getRejected, retry } from '@/db/repositories/attention'
import { cn } from '@/lib/utils'
import { syncEngine } from '@/sync/SyncEngine'
import { formatDateTime } from '@/utils'

import { SyncSignInDialog } from './SyncSignInDialog'

/**
 * Connectivity as a status, never a mode (§15.8 U5): a dot in the rail, and a panel with what is waiting and what the
 * server refused (U6). Nothing here ever blocks the till.
 */
export const SyncStatus = ({ className }: { className?: string }) => {
  const state = useSyncState()
  const [open, setOpen] = useState(false)
  const [signIn, setSignIn] = useState(false)
  const client = useQueryClient()
  const { data: rejected = [] } = useQuery({ queryKey: ['rejected', state.lastSyncedAt], queryFn: getRejected })

  const tone =
    rejected.length > 0 || state.status === 'auth' || state.status === 'maintenance' || state.status === 'suspended'
      ? 'bg-warning'
      : state.status === 'offline'
        ? 'bg-grey-500'
        : state.status === 'error'
          ? 'bg-error'
          : state.pending > 0 || state.status === 'syncing'
            ? 'bg-info'
            : 'bg-success'
  const label =
    state.status === 'offline'
      ? 'Offline'
      : state.status === 'auth'
        ? 'Sign in'
        : state.status === 'maintenance'
          ? 'Maintenance'
        : state.status === 'suspended'
          ? 'Suspended'
        : state.status === 'syncing'
        ? 'Syncing'
        : state.pending > 0
          ? `${state.pending} waiting`
          : 'Synced'

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className} aria-label={`Sync: ${label}`}>
        <span className="relative">
          {state.status === 'offline' ? <CloudOff className="size-5" /> : <RefreshCw className={cn('size-5', state.status === 'syncing' && 'animate-spin')} />}
          <span className={cn('absolute -right-0.5 -top-0.5 size-2 rounded-full', tone)} />
        </span>
        {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sync</DialogTitle>
            <DialogDescription>
              {state.lastSyncedAt ? `Last synced ${formatDateTime(state.lastSyncedAt)}` : 'Not synced yet'}
              {state.pending > 0 ? ` · ${state.pending} records waiting to send` : ''}
            </DialogDescription>
          </DialogHeader>
          {state.error && <p className="text-sm text-destructive">{state.error}</p>}
          {state.status === 'auth' && (
            <PosButton size="touch" variant="outline" onClick={() => setSignIn(true)}>
              Sign in to sync
            </PosButton>
          )}
          {rejected.length > 0 && (
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-sm font-medium text-ink">
                <AlertTriangle className="size-4 text-warning" /> Needs attention
              </p>
              <ul className="divide-y divide-border-subtle rounded-md border border-border">
                {rejected.map((r) => (
                  <li key={`${r.table}:${r.id}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate text-ink">{r.label}</span>
                      <span className="text-xs text-muted-foreground">Refused: {r.issue_fields.join(', ') || 'unknown reason'}</span>
                    </span>
                    <PosButton
                      size="default"
                      variant="secondary"
                      onClick={async () => {
                        await retry(r.table, r.id)
                        client.invalidateQueries({ queryKey: ['rejected'] })
                      }}
                    >
                      Send again
                    </PosButton>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <PosButton size="touch" onClick={() => syncEngine.requestSync()} disabled={state.status === 'syncing'}>
            <RefreshCw /> Sync now
          </PosButton>
        </DialogContent>
      </Dialog>
      <SyncSignInDialog open={signIn} onOpenChange={setSignIn} />
    </>
  )
}
